/**
 * Stepped hero search — iter 67ds rewrite.
 *
 * Walks the customer through the booking funnel in 4 streamlined steps:
 *   1) Pick-up city / venue   (was step 2)
 *   2) Return  city / venue   (was step 3)
 *   3) Pick-up date & time
 *   4) Return  date & time
 *
 * Below the stepper sits a "Driver aged 25–65" checkbox + age dropdown
 * (mirrors the mobile HeroMobileSearch driver block exactly).
 *
 * Country is no longer a manual step — it's INFERRED from the pick-up
 * location (locations come back as "City, Country" strings) and propagated
 * to the parent via `onCountryChange` so the home-page vendor-logo carousel
 * still narrows correctly.
 *
 * Colour palette mirrors the mobile card: amber-500 background, white
 * inner cells, black "Search cars" pill with amber text.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import { MapPin, MapPinned, CalendarDays, Search, Check, ChevronRight, Sparkles, UserCheck } from "lucide-react";

// Smart "next long weekend" default for the pickup/return pickers.
// Friday 17:00 → Monday 10:00 of the soonest weekend ≥24h away.
function _nextLongWeekend() {
  const now = new Date();
  const day = now.getDay();
  let daysToFri = (5 - day + 7) % 7;
  const candidate = new Date(now);
  candidate.setDate(candidate.getDate() + daysToFri);
  candidate.setHours(17, 0, 0, 0);
  const HOURS_24_MS = 24 * 60 * 60 * 1000;
  if (candidate.getTime() - now.getTime() < HOURS_24_MS) {
    candidate.setDate(candidate.getDate() + 7);
  }
  const end = new Date(candidate);
  end.setDate(end.getDate() + 3);
  end.setHours(10, 0, 0, 0);
  return { start: candidate, end };
}

function _toLocalInput(d) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function _toDatetimeLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function _snapToHalfHour(date) {
  const d = new Date(date);
  d.setSeconds(0, 0);
  const m = d.getMinutes();
  if (m < 15) d.setMinutes(0);
  else if (m < 45) d.setMinutes(30);
  else { d.setMinutes(0); d.setHours(d.getHours() + 1); }
  return d;
}

// Derive country from a location string like "Tirana, Albania" or
// "Tirana Airport, Albania". Falls back to empty string if no comma.
function _countryFromLocation(loc) {
  if (!loc || typeof loc !== "string") return "";
  const idx = loc.lastIndexOf(",");
  if (idx === -1) return "";
  return loc.slice(idx + 1).trim();
}

const AGE_OPTIONS = [
  { value: "21", label: "18–24 (young driver)" },
  { value: "26", label: "25–29" },
  { value: "30", label: "30–65" },
  { value: "66", label: "66+ (senior)" },
];
const DEFAULT_AGE = "30";

function StepHeader({ index, label, icon: Icon, active, complete, value, onEdit }) {
  return (
    <button
      type="button"
      onClick={onEdit}
      className={`w-full text-left px-3 py-2.5 rounded-xl border transition flex items-center gap-2.5 ${
        active
          ? "bg-white border-amber-300 ring-2 ring-amber-500/40 shadow-sm"
          : complete
          ? "bg-emerald-50/90 border-emerald-200 hover:brightness-95"
          : "bg-white/80 border-white/60 hover:bg-white opacity-90"
      }`}
      data-testid={`hero-step-${index}`}
    >
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 shadow-sm ${
        complete ? "bg-emerald-500 text-white" : active ? "bg-neutral-900 text-amber-400" : "bg-neutral-200 text-neutral-500"
      }`}>
        {complete ? <Check className="h-3.5 w-3.5" /> : index}
      </div>
      <Icon className={`h-4 w-4 shrink-0 ${complete ? "text-emerald-600" : active ? "text-amber-700" : "text-neutral-400"}`} />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider font-semibold text-neutral-500">{label}</div>
        <div className={`text-sm truncate ${complete ? "font-semibold text-neutral-900" : "text-neutral-400"}`}>
          {value || "—"}
        </div>
      </div>
    </button>
  );
}

export default function HeroStepperSearch({ onCountryChange } = {}) {
  const navigate = useNavigate();
  const [urlParams] = useSearchParams();

  const [step, setStep] = useState(1);
  const [pickupLoc, setPickupLoc] = useState(() => urlParams.get("pickup_location") || "");
  const [dropoffLoc, setDropoffLoc] = useState(() => urlParams.get("dropoff_location") || "");
  const [pickup, setPickup] = useState(() => {
    const seeded = _toDatetimeLocal(urlParams.get("start_date"));
    if (seeded) return seeded;
    return _toLocalInput(_nextLongWeekend().start);
  });
  const [dropoff, setDropoff] = useState(() => {
    const seeded = _toDatetimeLocal(urlParams.get("end_date"));
    if (seeded) return seeded;
    return _toLocalInput(_nextLongWeekend().end);
  });
  const [datesAutoDefault, setDatesAutoDefault] = useState(() =>
    !urlParams.get("start_date") && !urlParams.get("end_date")
  );

  // Driver-age block (matches mobile semantics exactly).
  const [standardAge, setStandardAge] = useState(true);
  const [age, setAge] = useState(DEFAULT_AGE);

  // Bubble country to parent whenever pick-up location changes so the
  // home-page vendor carousel + slider stay in sync. Falls back to "" if
  // the location has no comma (e.g. raw airport code) — the parent treats
  // empty as "all countries".
  useEffect(() => {
    onCountryChange?.(_countryFromLocation(pickupLoc));
  }, [pickupLoc, onCountryChange]);

  // Jump to the first incomplete step on mount for the "Modify search" round-trip.
  // If everything is already filled (e.g. dates auto-defaulted + locations
  // arrive via URL params), keep all popovers closed so the Search button
  // is immediately reachable.
  useEffect(() => {
    if (!pickupLoc) setStep(1);
    else if (!dropoffLoc) setStep(2);
    else if (!pickup) setStep(3);
    else if (!dropoff) setStep(4);
    else setStep(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stepsDone = useMemo(() => ({
    1: !!pickupLoc,
    2: !!dropoffLoc,
    3: !!pickup,
    4: !!dropoff,
  }), [pickupLoc, dropoffLoc, pickup, dropoff]);

  const canSearch = stepsDone[1] && stepsDone[2] && stepsDone[3] && stepsDone[4];

  const reset = (newStep) => setStep(newStep);

  const pickDropoff = (loc) => { setDropoffLoc(loc); setStep(3); };

  // Outside-click handler: close any open desktop popover when the user
  // taps outside the stepper (so the Search button is reachable and the
  // popover doesn't linger visually).
  const stepperRef = useRef(null);
  useEffect(() => {
    if (step === 0) return;
    const onDown = (e) => {
      if (!stepperRef.current) return;
      if (!stepperRef.current.contains(e.target)) setStep(0);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [step]);

  const goSearch = () => {
    if (!canSearch) return;
    const params = new URLSearchParams();
    const inferredCountry = _countryFromLocation(pickupLoc);
    if (inferredCountry) params.set("country", inferredCountry);
    if (pickupLoc) params.set("pickup_location", pickupLoc);
    if (dropoffLoc) params.set("dropoff_location", dropoffLoc);
    if (pickup) params.set("start_date", new Date(pickup).toISOString());
    if (dropoff) params.set("end_date", new Date(dropoff).toISOString());
    params.set("driver_age", standardAge ? DEFAULT_AGE : age);
    navigate(`/cars?${params.toString()}`);
  };

  return (
    <div
      ref={stepperRef}
      className="rounded-3xl shadow-2xl ring-1 ring-amber-700/40 bg-amber-500 p-4 sm:p-5"
      data-testid="hero-stepper"
    >
      {/* Header strip — black pill with amber accent text. */}
      <div className="flex items-center gap-2 mb-3 px-1">
        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-neutral-900 text-amber-400 text-[10px] font-bold uppercase tracking-wider shadow-sm">
          <Sparkles className="h-3 w-3" /> Step search
        </div>
        <div className="text-xs text-neutral-900/80 font-medium">Pick a city, then your dates.</div>
      </div>

      {/* Step header row — 4 visible stops.
          Iter 70e (desktop only): the PICK-UP / RETURN cards act as their
          own click-to-open dropdowns; the autocomplete floats directly below
          the tapped card instead of pushing the whole layout down. On mobile
          the behaviour is unchanged — the shared body row below still
          renders the step's content. */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
        <div className="relative">
          <StepHeader
            index={1}
            label="Pick-up"
            icon={MapPin}
            active={step === 1}
            complete={stepsDone[1]}
            value={pickupLoc}
            onEdit={() => reset(1)}
          />
          {step === 1 && (
            <div
              className="hidden sm:block absolute left-0 top-full mt-2 z-30 w-[min(28rem,calc(100vw-2rem))]"
              data-testid="hero-step-1-desktop-popover"
            >
              <div className="rounded-2xl border border-amber-200 bg-white p-4 shadow-2xl">
                <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5 text-neutral-900" /> Pick-up city or venue
                </div>
                <LocationAutocomplete
                  value={pickupLoc}
                  onChange={(v) => { setPickupLoc(v); if (!dropoffLoc) setDropoffLoc(v); }}
                  kind="pickup"
                  placeholder="Pick a city, airport or station…"
                  testId="hero-pickup-autocomplete-desktop"
                  inputClassName="rounded-xl h-11 pr-10"
                  showAllOnFocus
                />
                {pickupLoc && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                    <span>Selected:</span>
                    <span className="font-semibold text-neutral-900">{pickupLoc}</span>
                    <button
                      type="button"
                      onClick={() => setStep(2)}
                      className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                    >
                      Next <ChevronRight className="h-3 w-3 ml-0.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <StepHeader
            index={2}
            label="Return"
            icon={MapPinned}
            active={step === 2}
            complete={stepsDone[2]}
            value={dropoffLoc}
            onEdit={() => reset(2)}
          />
          {step === 2 && (
            <div
              className="hidden sm:block absolute left-0 top-full mt-2 z-30 w-[min(28rem,calc(100vw-2rem))]"
              data-testid="hero-step-2-desktop-popover"
            >
              <div className="rounded-2xl border border-amber-200 bg-white p-4 shadow-2xl">
                <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
                  <MapPinned className="h-3.5 w-3.5 text-neutral-900" /> Return city or venue
                </div>
                {pickupLoc && (
                  <button
                    type="button"
                    onClick={() => pickDropoff(pickupLoc)}
                    className="mb-2 text-[11px] font-semibold text-neutral-900 hover:underline"
                  >
                    ↩ Same as pick-up ({pickupLoc})
                  </button>
                )}
                <LocationAutocomplete
                  value={dropoffLoc}
                  onChange={(v) => setDropoffLoc(v)}
                  kind="dropoff"
                  placeholder="Pick a city, airport or station…"
                  testId="hero-dropoff-autocomplete-desktop"
                  inputClassName="rounded-xl h-11 pr-10"
                  showAllOnFocus
                />
                {dropoffLoc && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                    <span>Selected:</span>
                    <span className="font-semibold text-neutral-900">{dropoffLoc}</span>
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                    >
                      Next <ChevronRight className="h-3 w-3 ml-0.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <StepHeader
            index={3}
            label="Pick-up date"
            icon={CalendarDays}
            active={step === 3}
            complete={stepsDone[3]}
            value={pickup ? new Date(pickup).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
            onEdit={() => reset(3)}
          />
          {step === 3 && (
            <div
              className="hidden sm:block absolute right-0 sm:left-0 sm:right-auto top-full mt-2 z-30 w-[min(28rem,calc(100vw-2rem))]"
              data-testid="hero-step-3-desktop-popover"
            >
              <div className="rounded-2xl border border-amber-200 bg-white p-4 shadow-2xl">
                <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-neutral-900" /> When do you pick up?
                  {datesAutoDefault && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full" data-testid="hero-dates-default-caption">
                      <Sparkles className="h-2.5 w-2.5" /> next weekend · tap to change
                    </span>
                  )}
                </div>
                <Input
                  type="datetime-local"
                  value={pickup}
                  onChange={(e) => { setPickup(_toDatetimeLocal(_snapToHalfHour(e.target.value)) || e.target.value); setDatesAutoDefault(false); }}
                  step={1800}
                  className="rounded-xl h-11 border-neutral-300 focus-visible:ring-amber-500 w-full"
                  data-testid="hero-pickup-datetime-desktop"
                />
                {pickup && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                    <span>Selected:</span>
                    <span className="font-semibold text-neutral-900">{new Date(pickup).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
                    <button
                      type="button"
                      onClick={() => setStep(4)}
                      className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                    >
                      Next <ChevronRight className="h-3 w-3 ml-0.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="relative">
          <StepHeader
            index={4}
            label="Return date"
            icon={CalendarDays}
            active={step === 4}
            complete={stepsDone[4]}
            value={dropoff ? new Date(dropoff).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}
            onEdit={() => reset(4)}
          />
          {step === 4 && (
            <div
              className="hidden sm:block absolute right-0 top-full mt-2 z-30 w-[min(28rem,calc(100vw-2rem))]"
              data-testid="hero-step-4-desktop-popover"
            >
              <div className="rounded-2xl border border-amber-200 bg-white p-4 shadow-2xl">
                <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5 text-neutral-900" /> When will you return?
                  {datesAutoDefault && (
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">
                      <Sparkles className="h-2.5 w-2.5" /> next weekend · tap to change
                    </span>
                  )}
                </div>
                <Input
                  type="datetime-local"
                  value={dropoff}
                  onChange={(e) => { setDropoff(_toDatetimeLocal(_snapToHalfHour(e.target.value)) || e.target.value); setDatesAutoDefault(false); }}
                  step={1800}
                  className="rounded-xl h-11 border-neutral-300 focus-visible:ring-amber-500 w-full"
                  data-testid="hero-dropoff-datetime-desktop"
                />
                {dropoff && (
                  <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                    <span>Selected:</span>
                    <span className="font-semibold text-neutral-900">{new Date(dropoff).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
                    <button
                      type="button"
                      onClick={() => setStep(0)}
                      className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                      data-testid="hero-step-4-done"
                    >
                      Done <Check className="h-3 w-3 ml-0.5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Step body row.
          Iter 70e/71a — hidden on desktop for all steps because each step
          header now has its own anchored popover. Mobile still uses this
          shared body row for the active step's content. */}
      <div
        className={`rounded-2xl border border-amber-200 bg-white p-4 min-h-[120px] sm:hidden`}
        data-testid={`hero-step-${step}-body`}
      >
        {step === 1 && (
          <div>
            <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-neutral-900" /> Pick-up city or venue
            </div>
            {/* Iter 67dt — user-typed autocomplete replaces the pill list.
                Renter types the city / airport name; suggestions appear
                under the input after 2 characters. */}
            <LocationAutocomplete
              value={pickupLoc}
              onChange={(v) => { setPickupLoc(v); if (!dropoffLoc) setDropoffLoc(v); }}
              kind="pickup"
              placeholder="Pick a city, airport or station…"
              testId="hero-pickup-autocomplete"
              inputClassName="rounded-xl h-11 pr-10"
              showAllOnFocus
            />
            {pickupLoc && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                <span>Selected:</span>
                <span className="font-semibold text-neutral-900">{pickupLoc}</span>
                <button
                  type="button"
                  onClick={() => setStep(2)}
                  className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                  data-testid="hero-pickup-next"
                >
                  Next <ChevronRight className="h-3 w-3 ml-0.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div>
            <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
              <MapPinned className="h-3.5 w-3.5 text-neutral-900" /> Return city or venue
            </div>
            {pickupLoc && (
              <button
                type="button"
                onClick={() => pickDropoff(pickupLoc)}
                className="mb-2 text-[11px] font-semibold text-neutral-900 hover:underline"
                data-testid="hero-dropoff-same-as-pickup"
              >
                ↩ Same as pick-up ({pickupLoc})
              </button>
            )}
            <LocationAutocomplete
              value={dropoffLoc}
              onChange={(v) => setDropoffLoc(v)}
              kind="dropoff"
              placeholder="Pick a city, airport or station…"
              testId="hero-dropoff-autocomplete"
              inputClassName="rounded-xl h-11 pr-10"
              showAllOnFocus
            />
            {dropoffLoc && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                <span>Selected:</span>
                <span className="font-semibold text-neutral-900">{dropoffLoc}</span>
                <button
                  type="button"
                  onClick={() => setStep(3)}
                  className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                  data-testid="hero-dropoff-next"
                >
                  Next <ChevronRight className="h-3 w-3 ml-0.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-neutral-900" /> When do you pick up?
              {datesAutoDefault && (
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full" data-testid="hero-dates-default-caption-mobile">
                  <Sparkles className="h-2.5 w-2.5" /> next weekend · tap to change
                </span>
              )}
            </div>
            <Input
              type="datetime-local"
              value={pickup}
              onChange={(e) => { setPickup(_toDatetimeLocal(_snapToHalfHour(e.target.value)) || e.target.value); setDatesAutoDefault(false); }}
              step={1800}
              className="rounded-xl h-11 border-neutral-300 focus-visible:ring-amber-500 w-full"
              data-testid="hero-pickup-datetime"
            />
            {pickup && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                <span>Selected:</span>
                <span className="font-semibold text-neutral-900">{new Date(pickup).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="ml-auto text-[11px] font-semibold text-neutral-900 hover:underline inline-flex items-center"
                  data-testid="hero-next-to-return-date"
                >
                  Next <ChevronRight className="h-3 w-3 ml-0.5" />
                </button>
              </div>
            )}
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="text-xs font-semibold text-neutral-700 mb-2 flex items-center gap-1.5">
              <CalendarDays className="h-3.5 w-3.5 text-neutral-900" /> When will you return?
              {datesAutoDefault && (
                <span className="ml-auto inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-900 bg-amber-100 border border-amber-300 px-2 py-0.5 rounded-full">
                  <Sparkles className="h-2.5 w-2.5" /> next weekend · tap to change
                </span>
              )}
            </div>
            <Input
              type="datetime-local"
              value={dropoff}
              onChange={(e) => { setDropoff(_toDatetimeLocal(_snapToHalfHour(e.target.value)) || e.target.value); setDatesAutoDefault(false); }}
              step={1800}
              className="rounded-xl h-11 border-neutral-300 focus-visible:ring-amber-500 w-full"
              data-testid="hero-dropoff-datetime"
            />
            {dropoff && (
              <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
                <span>Selected:</span>
                <span className="font-semibold text-neutral-900">{new Date(dropoff).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Driver-age row — mirrors mobile semantics (checkbox + age select). */}
      <div className="mt-3 rounded-2xl border border-amber-200 bg-white px-3 py-2.5" data-testid="hero-driver-block">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="hero-standard-age-label">
            <Checkbox
              checked={standardAge}
              onCheckedChange={(v) => setStandardAge(!!v)}
              data-testid="hero-standard-age"
            />
            <UserCheck className="h-4 w-4 text-amber-700" />
            <span className="text-sm font-semibold text-neutral-800">
              Driver aged 25–65
            </span>
            <span className="text-[11px] text-neutral-500 ml-1">standard rates</span>
          </label>
          {!standardAge && (
            <div className="flex items-center gap-2" data-testid="hero-age-row">
              <span className="text-[10px] uppercase tracking-[0.16em] font-bold text-neutral-500">
                Driver's age
              </span>
              <Select value={age} onValueChange={setAge}>
                <SelectTrigger className="rounded-xl h-9 text-sm w-48" data-testid="hero-driver-age">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AGE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>

      {/* Search button — black pill with amber text, matches the mobile card. */}
      <div className="flex items-center justify-end mt-3">
        <Button
          onClick={goSearch}
          disabled={!canSearch}
          className="rounded-full h-11 px-6 font-bold disabled:opacity-50 bg-blue-600 hover:bg-blue-700 text-white shadow-lg shadow-blue-500/30"
          data-testid="hero-search-btn"
        >
          <Search className="h-4 w-4 mr-2" /> Search cars
        </Button>
      </div>
    </div>
  );
}
